import React from "react";
import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/ventrix/PublicLayout";

const COMPANY = "Ventrix Global Freights Ltd";
const UPDATED = "2 August 2026";

function LegalLayout({ title, intro, sections, testid }) {
  return (
    <div className="min-h-screen">
      <PublicHeader />
      <main className="vtx-container py-16 max-w-3xl" data-testid={testid}>
        <div className="vtx-eyebrow">/ Legal</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-black text-[#0E2F76] tracking-tighter">{title}</h1>
        <div className="mt-2 text-sm text-[#475569]">Last updated: {UPDATED}</div>

        {intro && <p className="mt-6 text-[15px] leading-relaxed text-[#0f172a]">{intro}</p>}

        <div className="mt-8 space-y-8">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-lg font-bold text-[#0E2F76]">{i + 1}. {s.h}</h2>
              <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-[#334155]">
                {s.p.map((para, j) => <p key={j}>{para}</p>)}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5">
                    {s.list.map((li, j) => <li key={j}>{li}</li>)}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 text-sm text-[#475569]">
          See also our <Link to="/privacy" className="vtx-link">Privacy Policy</Link> and{" "}
          <Link to="/terms" className="vtx-link">Terms of Use</Link>.
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export function Terms() {
  const sections = [
    { h: "Agreement to these terms", p: [
      `These Terms of Use ("Terms") govern your access to and use of the ${COMPANY} website, customer portal, and related services (together, the "Platform"). By creating an account or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.`,
    ]},
    { h: "Who we are", p: [
      `The Platform is operated by ${COMPANY}, a company incorporated in Kenya with its registered office at [Registered office address]. References to "we", "us" and "our" mean ${COMPANY}.`,
    ]},
    { h: "Accounts and eligibility", p: [
      "You must be at least 18 and able to form a binding contract to use the Platform. You are responsible for the accuracy of the information in your account and for keeping your credentials confidential.",
      "You are responsible for all activity under your account. Notify us immediately of any unauthorised use. We may enable multi-factor authentication and other security controls.",
    ]},
    { h: "The Platform and bookings", p: [
      "The Platform lets you obtain freight quotes, view and track shipments, receive notifications, and manage invoices. Bookings are created and managed by our operations team; where you submit a request, it is not confirmed until we accept it.",
      "Quotes are estimates based on the information provided and are not binding until a booking is confirmed. Rates may change if actual cargo details (weight, volume, class, route or surcharges) differ from those quoted.",
    ]},
    { h: "Carriage of goods", p: [
      "The physical carriage, forwarding and handling of goods is governed by our standard trading conditions and the applicable transport documents, conventions and Incoterms® for each shipment. Those documents prevail over these Terms in the event of conflict regarding carriage.",
      "You are responsible for ensuring cargo is accurately described, lawfully shipped, properly packed, and accompanied by required documentation.",
    ]},
    { h: "Prohibited and restricted goods", p: [
      "You must not tender goods that are illegal, sanctioned, or undeclared dangerous goods, or otherwise breach applicable export, import, customs or safety rules. We may refuse, hold, or dispose of non-compliant cargo and report it where required by law.",
    ]},
    { h: "Payments", p: [
      "Charges are payable in the currency and by the methods shown for your account (which may include card, mobile money, or bank transfer via third-party processors). You authorise us and our processors to charge the amounts due.",
      "Unless stated otherwise, prices exclude taxes, duties and third-party charges, which are your responsibility. Late or unpaid amounts may accrue interest and result in suspension of services.",
    ]},
    { h: "Acceptable use", p: [
      "You agree not to misuse the Platform, including by attempting to gain unauthorised access, interfering with its operation, scraping data, uploading malicious code, or using it to infringe others' rights or applicable law.",
    ]},
    { h: "Intellectual property", p: [
      `The Platform, including its software, design, and branding, is owned by ${COMPANY} or its licensors and is protected by intellectual-property laws. We grant you a limited, non-exclusive, revocable licence to use the Platform for its intended purpose. You retain rights in the data you submit.`,
    ]},
    { h: "Third-party services", p: [
      "The Platform integrates third-party services (for example payment processors, mapping/tracking, and email/SMS providers). Your use of those services may be subject to their own terms, and we are not responsible for them.",
    ]},
    { h: "Disclaimers", p: [
      "The Platform is provided \"as is\" and \"as available\". To the extent permitted by law, we disclaim implied warranties. Tracking positions, ETAs and quotes are estimates and may be inaccurate or delayed.",
    ]},
    { h: "Limitation of liability", p: [
      "To the maximum extent permitted by law, we are not liable for indirect, incidental, special or consequential losses, or for loss of profit, revenue, or data arising from your use of the Platform. Nothing limits liability that cannot be excluded by law. Liability for the carriage of goods is dealt with under the applicable trading conditions and conventions.",
    ]},
    { h: "Indemnity", p: [
      "You agree to indemnify us against claims, losses and costs arising from your breach of these Terms, your cargo, or your violation of applicable law or third-party rights.",
    ]},
    { h: "Suspension and termination", p: [
      "We may suspend or terminate access if you breach these Terms, create risk, or as required by law. You may stop using the Platform at any time. Provisions that by their nature should survive termination will survive.",
    ]},
    { h: "Governing law", p: [
      "These Terms are governed by the laws of Kenya, and the courts of Kenya have exclusive jurisdiction, without prejudice to mandatory consumer rights.",
    ]},
    { h: "Changes", p: [
      "We may update these Terms from time to time. Material changes will be notified through the Platform. Continued use after changes take effect constitutes acceptance.",
    ]},
    { h: "Contact", p: [
      "Questions about these Terms can be sent to legal@ventrixfreights.com.",
    ]},
  ];
  return <LegalLayout title="Terms of Use" testid="terms-page"
    intro={`Please read these Terms carefully. They set out the rules for using the ${COMPANY} Platform.`}
    sections={sections} />;
}

export function Privacy() {
  const sections = [
    { h: "Introduction", p: [
      `This Privacy Policy explains how ${COMPANY} ("we") collects, uses, and protects personal data when you use our website and customer portal (the "Platform"). We are the data controller for that data.`,
    ]},
    { h: "Data we collect", p: ["We collect:"], list: [
      "Account data — name, email, company, role, and authentication details (passwords are stored only as secure hashes).",
      "Shipment data — origins, destinations, parties (shipper/consignee), cargo details and contact details you provide for a shipment.",
      "Billing data — invoices and payment metadata. Card and mobile-money details are handled by our payment processors, not stored by us.",
      "Usage and device data — server log data and IP address, used to operate and secure the Platform.",
      "Communications — messages, notes, and notification preferences.",
    ]},
    { h: "How we use data", p: ["We use personal data to:"], list: [
      "Provide, operate and secure the Platform and your account.",
      "Process bookings, tracking, invoicing and payments.",
      "Send service messages and, where enabled, shipment notifications by email or SMS.",
      "Prevent fraud and abuse and comply with legal obligations.",
      "Maintain, troubleshoot and improve the Platform.",
    ]},
    { h: "Legal bases", p: [
      "Where applicable law (such as the GDPR) requires a legal basis, we rely on: performance of a contract (to provide the service); our legitimate interests (to secure and improve the Platform); and compliance with legal obligations (such as customs and accounting).",
    ]},
    { h: "Cookies", p: [
      "We use only strictly necessary cookies to sign you in and keep the Platform secure and working. These are required to deliver the service and are exempt from consent requirements. We do not use third-party advertising or analytics cookies, and we do not track you across other websites.",
    ]},
    { h: "Sharing your data", p: ["We share personal data only as needed with:"], list: [
      "Carriers and logistics partners, to move and track your cargo.",
      "Payment processors, to take payments.",
      "Email/SMS and mapping providers, to send notifications and show tracking.",
      "Hosting and infrastructure providers that run the Platform.",
      "Authorities and advisers where required by law.",
    ]},
    { h: "International transfers", p: [
      "Your data may be processed in countries other than your own. Where it is, we use appropriate safeguards (such as standard contractual clauses) as required by applicable law.",
    ]},
    { h: "Retention", p: [
      "We keep personal data for as long as needed to provide the service and to meet legal, tax, and dispute-resolution requirements, after which it is deleted or anonymised.",
    ]},
    { h: "Security", p: [
      "We use technical and organisational measures to protect data, including encryption of stored provider secrets, hashed passwords, access controls, and audit logging. No system is completely secure, so we cannot guarantee absolute security.",
    ]},
    { h: "Your rights", p: [
      "Subject to applicable law, you may have rights to access, correct, delete, or port your data, to object to or restrict certain processing, and to withdraw consent. To exercise these rights, contact us using the details below. You may also complain to your local data-protection authority.",
    ]},
    { h: "Children", p: [
      "The Platform is intended for business users and is not directed to children under 16. We do not knowingly collect their data.",
    ]},
    { h: "Changes", p: [
      "We may update this Policy from time to time and will post the updated version here with a new \"last updated\" date.",
    ]},
    { h: "Contact", p: [
      "For privacy questions or requests, contact our data protection contact at privacy@ventrixfreights.com, or write to us at [Registered office address], Kenya.",
    ]},
  ];
  return <LegalLayout title="Privacy Policy" testid="privacy-page"
    intro="Your privacy matters to us. This policy explains what we collect and why, and the choices you have."
    sections={sections} />;
}
